import { useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, Loader2, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STORE, waLink, telLink } from "@/lib/store";
import { CATEGORIES } from "@/lib/categories";
import { submitFormspree, hasFormspree } from "@/lib/api";

const initial = { name: "", phone: "", email: "", category: "", message: "" };

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
            toast.error("Please fill your name, phone and message.");
            return;
        }
        const composed = `Hi The Hardware Store, ${form.message.trim()}` +
            (form.category ? ` (Category: ${form.category})` : "") +
            ` — ${form.name.trim()}${form.phone.trim() ? `, ${form.phone.trim()}` : ""}` +
            (form.email.trim() ? `, ${form.email.trim()}` : "");

        try {
            setLoading(true);
            // If Formspree endpoint is configured, also email the owner.
            if (hasFormspree) {
                await submitFormspree({
                    name: form.name.trim(),
                    phone: form.phone.trim(),
                    email: form.email.trim(),
                    category: form.category,
                    message: form.message.trim(),
                    _subject: `New enquiry from ${form.name.trim()} · The Hardware Store`,
                });
            }
            // Always open WhatsApp with the composed message.
            window.open(waLink(composed), "_blank", "noopener,noreferrer");
            toast.success("Opening WhatsApp with your message…");
            setForm(initial);
            setDone(true);
        } catch (err) {
            toast.error("Could not send. Please try WhatsApp or call directly.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page">
            {/* Header */}
            <section className="bg-charcoal text-white pt-16 pb-20 md:pt-24 md:pb-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-blueprint opacity-60 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="uppercase tracking-[0.3em] text-xs font-semibold text-safety mb-4">Get in touch</p>
                    <h1 className="font-display font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
                        Talk to us. We'll help you find it.
                    </h1>
                    <p className="mt-6 text-white/70 max-w-xl">
                        Send us a quick message with what you need — tool, quantity, brand — and we'll
                        confirm availability and price on WhatsApp or call.
                    </p>
                </div>
            </section>

            {/* Body */}
            <section className="bg-background py-20 md:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Info column */}
                    <aside className="lg:col-span-4 space-y-4" data-testid="contact-info-column">
                        <InfoCard icon={Phone} title="Call us" testId="info-call">
                            <a href={telLink()} className="text-charcoal font-display font-bold text-xl hover:text-safety">
                                {STORE.phoneDisplay}
                            </a>
                            <p className="text-sm text-muted-foreground mt-1">Tap to dial from mobile.</p>
                        </InfoCard>

                        <InfoCard icon={MessageCircle} title="WhatsApp" testId="info-whatsapp" accent>
                            <a href={waLink()} target="_blank" rel="noreferrer" className="text-charcoal font-display font-bold text-xl hover:text-safety">
                                Chat with us
                            </a>
                            <p className="text-sm text-muted-foreground mt-1">Fastest way to get a quote.</p>
                        </InfoCard>

                        <InfoCard icon={Mail} title="Email" testId="info-email">
                            <a href={`mailto:${STORE.email}`} className="text-charcoal font-semibold break-all hover:text-safety">
                                {STORE.email}
                            </a>
                        </InfoCard>

                        <InfoCard icon={Clock} title="Support hours" testId="info-hours">
                            {STORE.hours.map(h => (
                                <div key={h.label} className="flex justify-between text-sm py-1">
                                    <span className="font-semibold text-charcoal">{h.label}</span>
                                    <span className="text-muted-foreground">{h.value}</span>
                                </div>
                            ))}
                        </InfoCard>
                    </aside>

                    {/* Form column */}
                    <div className="lg:col-span-8">
                        <div className="bg-white border border-border rounded-3xl p-6 md:p-10 shadow-sm">
                            {done ? (
                                <div className="py-14 text-center" data-testid="contact-success">
                                    <div className="w-16 h-16 rounded-full bg-safety/10 grid place-items-center mx-auto">
                                        <CheckCircle2 className="w-8 h-8 text-safety" />
                                    </div>
                                    <h2 className="mt-6 font-display font-extrabold text-3xl md:text-4xl text-charcoal">Message sent!</h2>
                                    <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                                        We usually reply within the hour during working hours. For instant help,
                                        WhatsApp us at {STORE.phoneDisplay}.
                                    </p>
                                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                                        <a href={waLink()} target="_blank" rel="noreferrer" data-testid="success-whatsapp-btn">
                                            <Button className="rounded-full h-11 px-6 bg-safety hover:bg-safety-hover text-white">
                                                <MessageCircle className="w-4 h-4 mr-2" /> Chat now
                                            </Button>
                                        </a>
                                        <Button
                                            data-testid="send-another-btn"
                                            variant="outline"
                                            className="rounded-full h-11 px-6"
                                            onClick={() => setDone(false)}
                                        >
                                            Send another
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <Field label="Your name *" htmlFor="name">
                                            <Input
                                                id="name"
                                                data-testid="contact-name-input"
                                                required
                                                value={form.name}
                                                onChange={update("name")}
                                                placeholder="e.g. Rohit Sharma"
                                                className="h-12 rounded-xl bg-background border-border focus-visible:ring-2 focus-visible:ring-safety focus-visible:border-safety"
                                            />
                                        </Field>
                                        <Field label="Phone *" htmlFor="phone">
                                            <Input
                                                id="phone"
                                                type="tel"
                                                data-testid="contact-phone-input"
                                                required
                                                value={form.phone}
                                                onChange={update("phone")}
                                                placeholder="+91 98xxxxxxxx"
                                                className="h-12 rounded-xl bg-background border-border focus-visible:ring-2 focus-visible:ring-safety focus-visible:border-safety"
                                            />
                                        </Field>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <Field label="Email (optional)" htmlFor="email">
                                            <Input
                                                id="email"
                                                type="email"
                                                data-testid="contact-email-input"
                                                value={form.email}
                                                onChange={update("email")}
                                                placeholder="you@example.com"
                                                className="h-12 rounded-xl bg-background border-border focus-visible:ring-2 focus-visible:ring-safety focus-visible:border-safety"
                                            />
                                        </Field>
                                        <Field label="Category (optional)" htmlFor="category">
                                            <Select value={form.category} onValueChange={update("category")}>
                                                <SelectTrigger id="category" data-testid="contact-category-select" className="h-12 rounded-xl bg-background border-border">
                                                    <SelectValue placeholder="Choose a category" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {CATEGORIES.map(c => (
                                                        <SelectItem key={c.slug} value={c.name} data-testid={`contact-category-option-${c.slug}`}>
                                                            {c.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                    </div>

                                    <Field label="What are you looking for? *" htmlFor="message">
                                        <Textarea
                                            id="message"
                                            data-testid="contact-message-input"
                                            required
                                            value={form.message}
                                            onChange={update("message")}
                                            rows={6}
                                            placeholder="Tell us the product, size, brand or quantity you need…"
                                            className="rounded-xl bg-background border-border focus-visible:ring-2 focus-visible:ring-safety focus-visible:border-safety resize-none"
                                        />
                                    </Field>

                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                                        <Button
                                            type="submit"
                                            disabled={loading}
                                            data-testid="contact-submit-btn"
                                            className="h-12 px-6 rounded-full bg-safety hover:bg-safety-hover text-white font-semibold btn-shine"
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                                                </>
                                            ) : (
                                                <>
                                                    <MessageCircle className="w-4 h-4 mr-2" /> Send via WhatsApp
                                                </>
                                            )}
                                        </Button>

                                        <a
                                            href={telLink()}
                                            data-testid="contact-call-alt"
                                        >
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="h-12 px-6 rounded-full border-charcoal/20 text-charcoal hover:bg-charcoal hover:text-white font-semibold w-full sm:w-auto"
                                            >
                                                <Phone className="w-4 h-4 mr-2" /> Call instead
                                            </Button>
                                        </a>
                                    </div>

                                    <p className="text-xs text-muted-foreground pt-2">
                                        By submitting, you agree to be contacted about your inquiry.
                                        We never share your details.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, htmlFor, children }) {
    return (
        <div>
            <Label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.2em] font-bold text-charcoal/70">
                {label}
            </Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function InfoCard({ icon: Icon, title, children, accent = false, testId }) {
    return (
        <div
            data-testid={testId}
            className={`rounded-2xl border p-5 transition-colors ${
                accent ? "bg-safety/[0.05] border-safety/30" : "bg-white border-border hover:border-charcoal/30"
            }`}
        >
            <div className="flex items-center gap-3 mb-2">
                <div className={`w-9 h-9 rounded-lg grid place-items-center ${accent ? "bg-safety text-white" : "bg-charcoal text-white"}`}>
                    <Icon className="w-4 h-4" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-charcoal/70">{title}</p>
            </div>
            <div>{children}</div>
        </div>
    );
}
